import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, getContent } from '@neke/core';
import type { ApiError} from '../api/client';
import { api, type AdminGrant, type AdminMatch, type AdminPool } from '../api/client';
import { useSession } from '../store/session';
import { loginOf } from '../login';

/**
 * Админка тоже мобильная: таблица пар на телефоне нечитаема, поэтому
 * карточки с решением по одной паре, без чекбоксов и массовых действий.
 */
export function Admin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, locale, ready } = useSession();
  const content = getContent(locale);

  const [pool, setPool] = useState<AdminPool | null>(null);
  const [matches, setMatches] = useState<AdminMatch[]>([]);
  // null — реестр ещё грузится: пустой массив с ходу показал бы «отчётов нет».
  const [grants, setGrants] = useState<AdminGrant[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Реестр отчётов: ссылка на пару и заметка про оплату («Kaspi, чек 21.08»).
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  // Ошибку действия с грантом держим отдельно от общей: неверная ссылка не
  // повод прятать всю страницу.
  const [reportError, setReportError] = useState<string | null>(null);

  const load = () => {
    void Promise.all([api.adminPool(), api.adminMatches()])
      .then(([p, m]) => {
        setPool(p);
        setMatches(m);
      })
      .catch((e: ApiError) => setError(e.message));
    // Реестр грузим отдельным запросом: его ошибка не должна уносить страницу
    // целиком вместе с формой выдачи доступа.
    void api
      .adminReports()
      .then(setGrants)
      .catch((e: ApiError) => setReportError(e.message));
  };

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate('/login?next=/admin');
      return;
    }
    // MATCHMAKER сюда больше не пускаем: доступ к /api/admin даёт список почт,
    // и роль без почты из списка получит 403 на каждом запросе.
    if (user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    load();
  }, [ready, user, navigate]);

  const recompute = () => {
    setBusy(true);
    void api
      .adminRecompute()
      .then(load)
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setBusy(false));
  };

  const decide = (id: string, status: 'APPROVED' | 'REJECTED' | 'SENT') => {
    void api.adminDecide(id, status).then(load).catch((e: ApiError) => setError(e.message));
  };

  // Повторное открытие закрытого гранта — тот же вызов, только ref = pairId.
  const openReport = (value: string, comment?: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setReportBusy(true);
    setReportError(null);
    void api
      .adminReportOpen(trimmed, comment?.trim() || undefined)
      .then(() => {
        setRef('');
        setNote('');
        load();
      })
      .catch((e: ApiError) => setReportError(e.message))
      .finally(() => setReportBusy(false));
  };

  const closeReport = (pairId: string) => {
    setReportBusy(true);
    setReportError(null);
    void api
      .adminReportClose(pairId)
      .then(load)
      .catch((e: ApiError) => setReportError(e.message))
      .finally(() => setReportBusy(false));
  };

  // Даты — в локали интерфейса: админка переведена и на казахский.
  const when = (iso: string) =>
    new Date(iso).toLocaleString(locale === 'kk' ? 'kk-KZ' : 'ru-RU', {
      dateStyle: 'short',
      timeStyle: 'short',
    });

  if (error) return <div className="page"><div className="card"><p>{error}</p></div></div>;

  return (
    <div className="page">
      <div className="stack">
        <h1>{t('admin.title')}</h1>
        <div className="btn-row">
          <Link to="/admin/funnel" className="btn btn-ghost">
            {t('funnel.title')}
          </Link>
          <Link to="/admin/log" className="btn btn-ghost">
            {t('audit.title')}
          </Link>
        </div>
      </div>

      {/* Ручная выдача доступа сверху: ею пользуются чаще, чем подбором. */}
      <div className="card stack">
        <h2>{t('admin.reports')}</h2>
        <label className="field">
          <span>{t('admin.reportRef')}</span>
          <input type="text" value={ref} onChange={(e) => setRef(e.target.value)} />
        </label>
        <p className="fine">{t('admin.reportRefHint')}</p>
        <label className="field">
          <span>{t('admin.reportNote')}</span>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <button
          className="btn btn-gold"
          onClick={() => openReport(ref, note)}
          disabled={reportBusy || ref.trim() === ''}
        >
          {reportBusy ? t('common.loading') : t('admin.reportOpen')}
        </button>
        {reportError && (
          <p className="fine" style={{ color: 'var(--alert)' }}>
            {reportError}
          </p>
        )}
      </div>

      {grants === null ? (
        // Про сорвавшуюся загрузку реестра уже сказано под формой — не дублируем.
        reportError ? null : <p className="muted">{t('common.loading')}</p>
      ) : grants.length === 0 ? (
        <p className="notice">{t('admin.reportEmpty')}</p>
      ) : (
        grants.map((g) => (
          <div className={`match ${g.revokedAt ? 'neutral' : 'introduce'}`} key={g.pairId}>
            <div className="match-head">
              <strong>
                {g.a.name}
                {g.b ? ` · ${g.b.name}` : ''}
              </strong>
              <span className="fine">{g.tier}</span>
            </div>

            <p className="fine">
              {loginOf(g.a)}
              {g.b ? ` · ${loginOf(g.b)}` : ''}
            </p>

            <p className="fine">
              {t('admin.reportOpenedAt')}: {when(g.grantedAt)} · {t('admin.reportBy')}:{' '}
              {g.grantedByName}
            </p>

            {g.note && <p className="fine">{g.note}</p>}

            {(g.hasPayment || !g.pairReady) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {g.hasPayment && <span className="flag">{t('admin.reportKaspi')}</span>}
                {/* Без второй анкеты отчёт не соберётся — грант сам по себе не поможет. */}
                {!g.pairReady && (
                  <span className="flag hard">{t('admin.reportPairIncomplete')}</span>
                )}
              </div>
            )}

            <p className="fine">
              <a href={g.reportUrl} target="_blank" rel="noopener noreferrer">
                {g.reportUrl}
              </a>
            </p>

            {g.revokedAt ? (
              <div className="btn-row">
                <span className="chip">
                  {t('admin.reportClosedAt')}: {when(g.revokedAt)}
                </span>
                <button
                  className="btn btn-gold"
                  onClick={() => openReport(g.pairId)}
                  disabled={reportBusy}
                >
                  {t('admin.reportReopen')}
                </button>
              </div>
            ) : (
              <div className="btn-row">
                <button
                  className="btn btn-ghost"
                  onClick={() => closeReport(g.pairId)}
                  disabled={reportBusy}
                >
                  {t('admin.reportClose')}
                </button>
              </div>
            )}
          </div>
        ))
      )}

      <div className="stack">
        {pool && (
          <p className="muted">
            {t('admin.pool')}: {pool.men} {t('admin.men')}, {pool.women} {t('admin.women')}
          </p>
        )}
        <button className="btn btn-primary" onClick={recompute} disabled={busy}>
          {busy ? t('common.loading') : t('admin.recompute')}
        </button>
      </div>

      {matches.length === 0 ? (
        <p className="notice">{t('admin.empty')}</p>
      ) : (
        matches.map((m) => (
          <div className={`match ${m.tier}`} key={m.id}>
            <div className="match-head">
              <strong>
                {m.a.name} · {m.b.name}
              </strong>
              <span className="fine">{m.score}</span>
            </div>

            {m.breakdown.reasons.slice(0, 3).map((r, i) => (
              <p className={`reason ${r.tone}`} key={i}>
                {format(content.reasons[r.code as keyof typeof content.reasons] ?? r.code, r.params)}
              </p>
            ))}

            {m.breakdown.flags.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {m.breakdown.flags.map((f, i) => (
                  <span className={`flag ${f.hard ? 'hard' : ''}`} key={i}>
                    {content.flags[f.code as keyof typeof content.flags] ?? f.code}
                  </span>
                ))}
              </div>
            )}

            {m.contacts && (
              <p className="fine">
                {m.contacts.a} · {m.contacts.b}
              </p>
            )}

            {m.status === 'NEW' ? (
              <div className="btn-row">
                <button className="btn btn-primary" onClick={() => decide(m.id, 'APPROVED')}>
                  {t('admin.approve')}
                </button>
                <button className="btn btn-ghost" onClick={() => decide(m.id, 'REJECTED')}>
                  {t('admin.reject')}
                </button>
              </div>
            ) : (
              <div className="btn-row">
                <span className="chip">{m.status}</span>
                {m.status === 'APPROVED' && (
                  <button className="btn btn-gold" onClick={() => decide(m.id, 'SENT')}>
                    {t('admin.sent')}
                  </button>
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
